import type {ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import type {UserAttachmentProps} from './user-attachment.types';
import {isImagePart} from './user-attachment.types';
import type {ImagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ImageThumbnail} from '@Components/thumbnail/components/image-thumbnail/image-thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {SkeletonBrickTheme} from '@Components/skeleton-brick';
import userMessageStyles from '../../user-message.module.scss';
import attachmentStyles from './user-attachment.module.scss';
import {getUniqueKeyForImagePart, getUrlForImagePart} from '@Libraries/ai-chat.library';
import {ClickableDiv} from '@Components/clickable-div';
import {isVideoUrl} from '@Utils/media.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {isReattachableImagePart} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {SwipeableReattachWrapper} from '@Components/ai-chat-window/components/swipeable-reattach-wrapper/swipeable-reattach-wrapper';

export function UserAttachment({part, partKey, onImageClick, onReattach, isMobile}: UserAttachmentProps): ReactNode {
  const [mediaSrc, setMediaSrc] = useState<string>('');

  useEffect(() => {
    if (!isImagePart(part)) {
      return;
    }

    const loadMediaUrl = async (): Promise<void> => {
      try {
        const url = await getUrlForImagePart(part, true);
        setMediaSrc(url);
      } catch (error) {
        console.error('Failed to load media URL', error);
      }
    };

    void loadMediaUrl();
  }, [part]);

  const getVideoPreview = (): ReactNode => {
    return <video src={mediaSrc} className={userMessageStyles.chatPromptAttachment} muted />;
  };

  const getImagePreview = (): ReactNode => {
    return (
      <ImageThumbnail
        data={{
          type: ThumbnailType.IMAGE,
          showSkeletonLoading: true,
          skeletonLoadingTheme: SkeletonBrickTheme.LIGHT,
          imageSrc: mediaSrc,
        }}
        className={userMessageStyles.chatPromptAttachment}
      />
    );
  };

  const getReattachIcon = (imagePart: ImagePart): ReactNode => {
    if (!isReattachableImagePart(imagePart)) {
      return null;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className="spacing-m-b-1 _rounded-arrow">{window.i18next.t('pmwjs_reattach')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
      >
        <div className={attachmentStyles.reattachButtonWrapper}>
          <Icon
            className="content-body-white"
            icon="icon-paperclip"
            type={IconType.DARK_GHOST}
            size={IconSize.SIZE_ICON_16}
            shape={IconShape.CIRCLE}
            onClick={(e) => {
              e.stopPropagation();
              onReattach(imagePart, mediaSrc);
            }}
            ariaLabel={window.i18next.t('pmwjs_reattach')}
          />
        </div>
      </OverlayTrigger>
    );
  };

  const getAttachment = (imagePart: ImagePart): ReactNode => {
    if (!mediaSrc) {
      return null;
    }

    const isVideo = isVideoUrl(mediaSrc);

    return (
      <ClickableDiv
        key={partKey}
        onClick={() => {
          onImageClick(getUniqueKeyForImagePart(imagePart));
        }}
        style={{cursor: 'pointer'}}
      >
        <SwipeableReattachWrapper
          onReattach={
            isReattachableImagePart(imagePart)
              ? (): void => {
                  onReattach(imagePart, mediaSrc);
                }
              : undefined
          }
          enabled={!!isMobile}
          className={`${attachmentStyles.attachmentContainer} spacing-m-l-2`}
        >
          {isVideo ? getVideoPreview() : getImagePreview()}
          <div className={attachmentStyles.imageHoverOverlay} />
          {getReattachIcon(imagePart)}
        </SwipeableReattachWrapper>
      </ClickableDiv>
    );
  };

  if (!isImagePart(part)) {
    return null;
  }

  return getAttachment(part);
}
